import { Injectable } from '@angular/core'
import { IJobApplicationDialogueView, IJobApplicationView, IJobPostApplicationViews } from '@employer/app/models/job-application-views.model'
import { selectors } from '@employer/app/store/selectors'
import { isNotNil, omit } from '@engineering11/utility'
import { E11ErrorHandlerService, E11Logger } from '@engineering11/web-api-error'
import { ComponentStore } from '@ngrx/component-store'
import { createEntityAdapter, EntityAdapter, EntityState } from '@ngrx/entity'
import { tapResponse } from '@ngrx/operators'
import { Store } from '@ngrx/store'
import { combineLatest, distinctUntilChanged, filter, from, map, mergeMap, Observable, of, switchMap, take, tap } from 'rxjs'
import { buildApplicationViewsId, JobApplicationViewsRepository } from '../services/job-application-views.repository'

export interface JobApplicationViewsState extends EntityState<IJobPostApplicationViews> {
  lastApplicationViewed: string | null
}

export const applicationViewsAdapter: EntityAdapter<IJobPostApplicationViews> = createEntityAdapter<IJobPostApplicationViews>({
  selectId: views => views.jobPostId, // Because these will be unique per user
})

const initialState: JobApplicationViewsState = applicationViewsAdapter.getInitialState({ lastApplicationViewed: null })
const applicationViewsSelectors = applicationViewsAdapter.getSelectors()

@Injectable({
  providedIn: 'root',
})
export class JobApplicationViewsStore extends ComponentStore<JobApplicationViewsState> {
  private userId$ = this.store.select(selectors.getCurrentUserId).pipe(filter(isNotNil), distinctUntilChanged())

  constructor(
    private store: Store,
    private applicationViewsRepository: JobApplicationViewsRepository,
    private logger: E11Logger,
    private errorHandler: E11ErrorHandlerService
  ) {
    super(initialState)
  }

  // Selectors
  private readonly applicationViewsForJob$ = (jobPostId: string) => this.select(s => applicationViewsSelectors.selectEntities(s)[jobPostId])
  readonly applicationViewedForJob$ = (jobPostId: string, applicationId: string) =>
    this.select(s => applicationViewsSelectors.selectEntities(s)[jobPostId]?.applicationIds?.includes(applicationId) ?? false)
  readonly applicationIdsViewedForJob$ = (jobPostId: string) =>
    this.select(s => applicationViewsSelectors.selectEntities(s)[jobPostId]?.applicationIds || [])
  readonly dialogueIdsViewedForJob$ = (jobPostId: string) =>
    this.select(s => applicationViewsSelectors.selectEntities(s)[jobPostId]?.dialogueIds || [])

  readonly lastApplicationViewed$ = this.select(s => s.lastApplicationViewed)

  // Effects

  readonly onGetForJobPostIfNotFetched = this.effect((jobPostId$: Observable<string>) =>
    jobPostId$.pipe(
      switchMap(jobPostId => {
        return this.applicationViewsForJob$(jobPostId).pipe(
          take(1),
          tap(views => {
            if (!views) this.onGetForJobPost(jobPostId)
            else this.logger.debug(`Store: views already loaded for job ${jobPostId}, no need to re-fetch`)
          })
        )
      })
    )
  )

  readonly onGetForJobPost = this.effect((jobPostId$: Observable<string>) =>
    combineLatest([jobPostId$, this.userId$]).pipe(
      switchMap(([jobPostId, userId]) => {
        return this.applicationViewsRepository.getForUserByJob(jobPostId, userId).pipe(
          tapResponse(
            response => {
              if (response) this.onViewsFetchedForJob(response)
            },
            (error: Error) => this.errorHandler.handleError(error)
          )
        )
      })
    )
  )

  readonly onMarkApplicationViewed = this.effect((view$: Observable<Omit<IJobApplicationView, 'userId'>>) =>
    combineLatest([this.userId$, view$]).pipe(
      map(([userId, view]) => ({ ...view, userId })),
      mergeMap(view => {
        this.updateLastApplicationViewed(view.applicationId)
        // skip write if already written
        const alreadyViewed$ = this.applicationViewsForJob$(view.jobPostId).pipe(
          map(views => views?.applicationIds?.includes(view.applicationId) ?? false),
          take(1)
        )
        return alreadyViewed$.pipe(
          switchMap(alreadyViewed => {
            if (alreadyViewed) {
              this.logger.debug(`Store onMarkApplicationViewed: already viewed application ${view.applicationId} for job ${view.jobPostId}`)
              return of()
            }
            return from(this.applicationViewsRepository.markApplicationViewed(view)).pipe(
              tapResponse(
                response => this.onApplicationViewed(view),
                (error: Error) => this.errorHandler.handleError(error)
              )
            )
          })
        )
      })
    )
  )

  readonly onMarkDialogueViewed = this.effect((view$: Observable<Omit<IJobApplicationDialogueView, 'userId'>>) =>
    combineLatest([this.userId$, view$]).pipe(
      map(([userId, view]) => ({ ...view, userId })),
      mergeMap(view => {
        // skip write if already written
        const alreadyViewed$ = this.applicationViewsForJob$(view.jobPostId).pipe(
          map(views => views?.dialogueIds?.includes(view.dialogueId) ?? false),
          take(1)
        )
        return alreadyViewed$.pipe(
          switchMap(alreadyViewed => {
            if (alreadyViewed) {
              this.logger.debug(`Store onMarkDialogueViewed: already viewed interview session ${view.dialogueId} for job ${view.jobPostId}`)
              return of()
            }
            return from(this.applicationViewsRepository.markApplicationDialogueViewed(view)).pipe(
              tapResponse(
                response => this.onApplicationDialogueViewed(view),
                (error: Error) => this.errorHandler.handleError(error)
              )
            )
          })
        )
      })
    )
  )

  // Reducers

  private readonly onViewsFetchedForJob = this.updater((state, views: IJobPostApplicationViews) =>
    applicationViewsAdapter.setOne(views, { ...state, loaded: true })
  )

  private readonly onApplicationViewed = this.updater((state, view: IJobApplicationView) => {
    const views = state.entities[view.jobPostId]

    const newViews: IJobPostApplicationViews = views
      ? { ...views, applicationIds: views.applicationIds?.concat([view.applicationId]) }
      : viewToViews(view)
    return applicationViewsAdapter.upsertOne(newViews, state)
  })

  private readonly onApplicationDialogueViewed = this.updater((state, view: IJobApplicationDialogueView) => {
    const views = state.entities[view.jobPostId]

    const newViews: IJobPostApplicationViews =
      views && views.dialogueIds
        ? { ...views, dialogueIds: views.dialogueIds.concat([view.dialogueId]) }
        : { ...viewToViews(view), applicationIds: views?.applicationIds ?? [] }
    return applicationViewsAdapter.upsertOne(newViews, state)
  })

  private updateLastApplicationViewed = this.updater((state, applicationId: string) => ({
    ...state,
    lastApplicationViewed: applicationId,
  }))
}

function viewToViews(view: IJobApplicationView | IJobApplicationDialogueView): IJobPostApplicationViews {
  const id = buildApplicationViewsId(view)

  if ('dialogueId' in view) {
    return { ...omit(view, 'dialogueId'), id, dialogueIds: [view.dialogueId], applicationIds: [] }
  }

  return { ...omit(view, 'applicationId'), id, applicationIds: [view.applicationId] }
}
